/**
 * Documents collection — proposals and contracts on top of the generic
 * store. Wraps `db` so views deal in `BusinessDoc` records and so doc
 * creation goes through a single template-driven path.
 *
 * Proposals attach to prospects (people); contracts attach to customers
 * (accounts). The data model doesn't enforce that pairing — any doc can
 * attach to any entity — but the templates and default statuses lean
 * into the common case.
 */

import { db, nowIso, uuid } from './store';
import { seedDocuments } from './seed';
import type {
  BusinessDoc,
  DocumentEntityType,
  DocumentKind,
  DocumentStatus,
} from './types';
import { spinUpOnboarding } from '../crm/playbook';
import { computeWinLoss } from '../crm/winloss';

export async function listDocuments(): Promise<BusinessDoc[]> {
  return db.list('documents');
}

export async function getDocument(id: string): Promise<BusinessDoc | null> {
  return db.get('documents', id);
}

/** All documents attached to a given entity, newest-first. */
export async function listDocumentsForEntity(
  entityType: DocumentEntityType,
  entityId: string,
): Promise<BusinessDoc[]> {
  const all = await db.list('documents');
  return all
    .filter((d) => d.entityType === entityType && d.entityId === entityId)
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
}

export interface NewDocumentInput {
  kind: DocumentKind;
  title?: string;
  entityType: DocumentEntityType;
  entityId: string;
  entityName: string;
  /** Provide to skip template generation. */
  body?: string;
  value?: number;
  validUntil?: string;
  ownerName?: string;
  createdBy?: string;
  /**
   * Visual template id stamped on the doc (see crm/proposalTemplates for
   * the branded proposal designs). Defaults to the kind's legacy default
   * so existing callers keep rendering exactly as before.
   */
  templateId?: string;
  /** Extra context fed to the template. */
  contactName?: string;
  summary?: string;
  /**
   * Client-specific "What we found" bullets. When omitted, createDocument
   * derives them from the live store (enrichment signals, stage, activity)
   * so every proposal reads like it was written about THIS client — which
   * it was. Falls back to the generic findings only when the store knows
   * nothing useful.
   */
  findings?: string[];
}

/**
 * Pull what the CRM actually knows about the entity and turn it into
 * client-specific findings + summary for the proposal template. This is
 * what separates "a template" from "a proposal about you": enrichment
 * signals, pipeline position, deal size, and relationship history all
 * feed the document. Best-effort — any failure falls back to generic copy.
 */
async function deriveProposalContext(
  input: NewDocumentInput,
): Promise<{ findings?: string[]; summary?: string }> {
  try {
    if (input.entityType === 'person') {
      const p = await db.get('people', input.entityId);
      if (!p) return {};
      const findings: string[] = [];
      const e = p.enrichment;
      if (e?.status === 'done' && e.summary) {
        findings.push(`**Our read on ${p.company || p.name}.** ${e.summary}`);
      }
      for (const signal of (e?.signals ?? []).slice(0, 3)) {
        findings.push(`**Live signal.** ${signal}`);
      }
      const touches = p.activity?.length ?? 0;
      if (touches > 0) {
        const meetings = p.activity!.filter((a) => a.kind === 'meeting').length;
        findings.push(
          `**What our conversations told us.** Across ${touches} touchpoint${touches === 1 ? '' : 's'}${meetings ? ` (${meetings} meeting${meetings === 1 ? '' : 's'})` : ''}, the consistent theme is execution capacity — the plan is sound, the hours to run it are not there.`,
        );
      }
      if (p.estimatedValue) {
        findings.push(
          `**The size of the prize.** We scoped this engagement against the ${fmtUsd(p.estimatedValue)} opportunity we mapped together — the investment below is sized to pay for itself inside the first term.`,
        );
      }
      // Lead with what actually closes our deals (and quietly answer the
      // most common reason they slip) — grounded in real win-loss history.
      try {
        const wl = computeWinLoss(await db.list('people'));
        const topWin = wl.wonReasons.find((r) => r.reason !== '(no reason given)');
        const topLoss = wl.lostReasons.find((r) => r.reason !== '(no reason given)');
        if (wl.winRate !== null && topWin) {
          const counter = topLoss
            ? ` We also designed it to address the objection we hear most — ${topLoss.reason.toLowerCase()} — head-on.`
            : '';
          findings.push(
            `**Why teams say yes.** Across our closed engagements (${wl.winRate}% win rate), the driver clients cite most is **${topWin.reason.toLowerCase()}** — which is exactly what this proposal is built to deliver.${counter}`,
          );
        }
      } catch {
        /* win-loss is a bonus finding; never block proposal generation */
      }
      const summary =
        e?.status === 'done' && e.summary
          ? `${e.summary} Argo IQ deploys an AI operating layer — agents, an AQI scoring engine, and managed delivery — built around exactly that reality.`
          : undefined;
      return { findings: findings.length >= 2 ? findings : undefined, summary };
    }
    // Account: ground the findings in the live workflows + recent audit.
    const a = await db.get('accounts', input.entityId);
    if (!a) return {};
    const findings: string[] = [];
    if (a.workflows.length > 0) {
      const stages = a.workflows.map((w) => `${w.name} (${w.stage})`).slice(0, 3);
      findings.push(
        `**Where the work stands.** ${a.workflows.length} active workflow${a.workflows.length === 1 ? '' : 's'} with ${a.name} today — ${stages.join(', ')} — each one a surface the operating layer takes over.`,
      );
    }
    const recent = a.audit.slice(-30).length;
    if (recent > 0) {
      findings.push(
        `**The pace is real.** ${recent} recorded account events in the recent window — momentum worth protecting with systems instead of memory.`,
      );
    }
    return { findings: findings.length >= 2 ? findings : undefined };
  } catch {
    return {};
  }
}

/**
 * Create a document. When `body` is omitted it is generated from the
 * built-in template for the kind, filled with whatever context the
 * caller supplied — plus, for proposals, client-specific findings derived
 * from the live store.
 */
export async function createDocument(
  input: NewDocumentInput,
): Promise<BusinessDoc> {
  const ts = nowIso();
  const title =
    input.title?.trim() ||
    (input.kind === 'proposal'
      ? `Proposal — ${input.entityName}`
      : `Service Agreement — ${input.entityName}`);
  let body = input.body;
  if (body == null) {
    const derived =
      input.kind === 'proposal' && !input.findings
        ? await deriveProposalContext(input)
        : {};
    body = renderTemplate({
      ...input,
      findings: input.findings ?? derived.findings,
      summary: input.summary ?? derived.summary,
    });
  }
  const record: BusinessDoc = {
    id: uuid(),
    kind: input.kind,
    title,
    status: 'draft',
    entityType: input.entityType,
    entityId: input.entityId,
    entityName: input.entityName,
    body,
    value: input.value,
    validUntil: input.validUntil,
    ownerName: input.ownerName,
    templateId:
      input.templateId ??
      (input.kind === 'proposal' ? 'proposal-default' : 'contract-default'),
    createdBy: input.createdBy,
    createdAt: ts,
    updatedAt: ts,
  };
  await db.add('documents', record);
  return record;
}

export async function updateDocument(
  id: string,
  patch: Partial<Omit<BusinessDoc, 'id' | 'createdAt'>>,
): Promise<BusinessDoc> {
  return db.update('documents', id, { ...patch, updatedAt: nowIso() });
}

/**
 * Set a document's status and potentially trigger downstream automation.
 * When a proposal or contract first transitions to a won state ('accepted'
 * for proposals, 'signed' for contracts), stamp `acceptedAt` and
 * fire-and-forget the onboarding playbook spin-up (creates visible,
 * reversible tasks so it stays auditable and can be undone by deletion).
 */
export async function setDocumentStatus(
  id: string,
  status: DocumentStatus,
): Promise<BusinessDoc> {
  const prior = await db.get('documents', id);
  // A "won" transition is entering accepted/signed from some other status.
  // Per STATUSES_BY_KIND, 'accepted' only exists on proposals and 'signed'
  // only on contracts, so this single check covers BOTH a proposal being
  // accepted AND a contract being signed. (The previous guard also required
  // `kind === 'proposal'`, which made the 'signed' branch unreachable — a
  // signed contract never spun up onboarding. Fixed.)
  const enteringWonState =
    (status === 'accepted' || status === 'signed') && prior?.status !== status;

  const patch: Partial<BusinessDoc> = { status, updatedAt: nowIso() };
  // Record the real close moment the first time the doc is won, so the
  // playbook anchors due dates to "now" instead of the (possibly stale)
  // draft date. Preserve the original on any later status churn.
  if (enteringWonState && !prior?.acceptedAt) {
    patch.acceptedAt = nowIso();
  }
  const updated = await db.update('documents', id, patch);

  if (enteringWonState) {
    spinUpOnboarding(updated).catch(() => undefined);
  }

  return updated;
}

export async function deleteDocument(id: string): Promise<void> {
  await db.remove('documents', id);
}

export function subscribeDocuments(
  fn: (records: BusinessDoc[]) => void,
): () => void {
  return db.subscribe('documents', fn);
}

/**
 * Seed the documents collection on first load so the Contracts view has
 * real proposals & contracts to filter/open/export. Idempotent: only
 * runs when the collection is empty.
 */
export async function bootstrapDocumentsIfEmpty(): Promise<void> {
  const existing = await db.list('documents');
  if (existing.length > 0) return;
  await db.replaceAll('documents', seedDocuments);
}

/** Reset the documents collection back to seed data. Useful for dev. */
export async function resetDocumentsToSeed(): Promise<void> {
  await db.replaceAll('documents', seedDocuments);
}

// --- Templates ------------------------------------------------------------

/** Status values that make sense per kind, for the editor dropdown. */
export const STATUSES_BY_KIND: Record<DocumentKind, DocumentStatus[]> = {
  proposal: ['draft', 'sent', 'viewed', 'accepted', 'declined', 'expired'],
  contract: ['draft', 'sent', 'viewed', 'signed', 'declined', 'expired'],
};

function fmtUsd(n?: number): string {
  if (n == null) return '$_____';
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

function today(): string {
  return new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/** Split a USD total into a sensible engagement structure for the body. */
function pricing(value?: number): { setup: number; monthly: number; term: number } {
  // Default to a $48k annual engagement when unspecified.
  const total = value && value > 0 ? value : 48_000;
  // ~25% one-time activation, remainder over 12 months.
  const setup = Math.round((total * 0.25) / 500) * 500;
  const monthly = Math.round((total - setup) / 12 / 100) * 100;
  return { setup, monthly, term: 12 };
}

/**
 * Render the markdown body for a new document. These are real, usable
 * defaults written in the ArgoIQ voice — not placeholders. The body stays
 * editable markdown; `renderBrandedDocHtml` wraps it in the branded
 * cover/print chrome for preview and PDF export.
 */
export function renderTemplate(input: NewDocumentInput): string {
  const who = input.contactName?.trim() || input.entityName;
  const owner = input.ownerName?.trim() || 'Grant Walker';
  const client = input.entityName;
  const total = fmtUsd(input.value && input.value > 0 ? input.value : 48_000);
  const { setup, monthly, term } = pricing(input.value);
  const summary =
    input.summary?.trim() ||
    `${client} is moving faster than its current tooling can keep up with. Argo IQ deploys an AI operating layer — agents, an AQI scoring engine, and managed delivery — that compounds your team's output instead of adding headcount.`;

  if (input.kind === 'proposal') {
    return [
      '## Executive summary',
      '',
      summary,
      '',
      `This proposal outlines a ${term}-month engagement to stand up that operating layer for ${client}, measured against a single number: your **AQI score** — the composite of velocity, quality, and coverage we improve every sprint.`,
      '',
      '## The problem we solve',
      '',
      `- **Work outpaces visibility.** Tickets, deals, and follow-ups slip because nothing watches the whole board at once.`,
      `- **Knowledge lives in heads.** Onboarding, audits, and decisions stall waiting on the one person who remembers.`,
      `- **Manual ops don't scale.** Every new customer adds linear overhead instead of leveraging what you already built.`,
      '',
      '## What we found',
      '',
      `Before writing a line of this proposal we looked at how ${client} actually runs today. Here's what stood out:`,
      '',
      ...(input.findings && input.findings.length > 0
        ? input.findings.map((f) => `- ${f}`)
        : [
            `- **The team is the bottleneck, not the strategy.** The plan is sound; execution is gated on a handful of people doing work a system should own.`,
            '- **Signal is there, but scattered.** The data needed to act early already exists — it just lives across tools that never talk to each other.',
            '- **Every win is manual.** Outcomes depend on someone remembering to follow up, check the queue, or chase the deal. That does not compound.',
          ]),
      '',
      'The opportunity is straightforward: turn that manual effort into an operating layer that runs whether or not anyone is watching.',
      '',
      '## Scope & approach',
      '',
      '1. **Discovery & AQI baseline** — Audit current systems, data, and workflows. Establish the starting AQI score and the target.',
      '2. **Agent deployment** — Stand up ticket, research, and outreach agents wired to your real data, with confirm-each-write controls.',
      '3. **Integration & enablement** — Connect your CRM, comms, and knowledge base. Train the team on the operating loop.',
      '4. **Optimization** — Weekly reviews against the AQI score; tune agents, close gaps, expand coverage.',
      '',
      '## What you get',
      '',
      `- A deployed Argo OS workspace tailored to ${client}`,
      '- Always-on agents that surface what needs attention before it slips',
      '- An AQI scorecard reviewed every sprint',
      '- Managed delivery from the Argo team — not a tool you maintain alone',
      '',
      '## Engagement timeline',
      '',
      '| Phase | Timing | Outcome |',
      '| --- | --- | --- |',
      '| Discovery & baseline | Weeks 1–2 | AQI baseline set, target agreed, first integrations mapped |',
      '| First agents live | Weeks 2–4 | Ticket + outreach agents working real data with confirm-each controls |',
      '| Integration & enablement | Weeks 4–8 | CRM, comms, and knowledge base connected; team trained on the loop |',
      `| Optimization | Ongoing | Sprint reviews against the AQI score; coverage expands every cycle |`,
      '',
      '## Investment',
      '',
      '| Component | Amount |',
      '| --- | --- |',
      `| Activation & onboarding (one-time) | ${fmtUsd(setup)} |`,
      `| Managed engagement (${term} months) | ${fmtUsd(monthly)} / mo |`,
      `| **Total ${term}-month value** | **${total}** |`,
      '',
      '## Our guarantee',
      '',
      `> If your AQI score has not measurably improved within the first 90 days, we keep working at no additional cost until it does. We are not selling software you have to operate — we are accountable for the outcome alongside ${client}.`,
      '',
      '## Next steps',
      '',
      '1. Align on scope and the target AQI score',
      '2. Counter-sign to kick off discovery',
      '3. First agents live within the opening sprint',
      '',
      '## Accept this proposal',
      '',
      `> Ready to move? Counter-sign below and we begin discovery this week. This proposal is valid until ${input.validUntil || '30 days from the date above'}.`,
      '',
      `| Argo IQ | ${client} |`,
      '| --- | --- |',
      `| ${owner} | ${who} |`,
      '| _________________ | _________________ |',
      '| Date: __________ | Date: __________ |',
      '',
      '---',
      '',
      `_Prepared for ${who} by ${owner}, Argo IQ — ${today()}. Valid until ${input.validUntil || '30 days from the date above'}._`,
    ].join('\n');
  }

  // contract — real, usable default clauses
  return [
    `**Between:** Argo IQ, Inc. ("Provider") and ${client} ("Client")`,
    `**Effective date:** ${today()}`,
    `**Engagement lead:** ${owner}`,
    '',
    '## 1. Services',
    '',
    `Provider will deliver the Argo OS platform and managed AI-operations services as described in the accompanying proposal and any mutually agreed statement of work, including agent deployment, integration, and ongoing optimization against the Client's AQI score.`,
    '',
    '## 2. Term',
    '',
    `This Agreement begins on the Effective Date and continues for an initial term of ${term} months, renewing in successive ${term}-month terms unless either party gives 30 days' written notice before renewal.`,
    '',
    '## 3. Fees & payment',
    '',
    '| Item | Amount | Schedule |',
    '| --- | --- | --- |',
    `| Activation & onboarding | ${fmtUsd(setup)} | Due on signature |`,
    `| Managed engagement | ${fmtUsd(monthly)} / mo | Monthly, net 15 |`,
    '',
    `Total committed value over the initial term is **${total}**. Invoices unpaid after 30 days may pause active services.`,
    '',
    '## 4. Service levels',
    '',
    '- Critical (P0) issues acknowledged within 4 business hours.',
    '- Standard requests addressed within 2 business days.',
    '- Sprint review and AQI scorecard delivered every two weeks.',
    '',
    '## 5. Intellectual property',
    '',
    'Provider retains all rights to the Argo OS platform, agents, and pre-existing IP. Client owns its data and any deliverables created specifically for Client under this Agreement, granted upon payment in full.',
    '',
    '## 6. Confidentiality & data protection',
    '',
    "Each party will protect the other's confidential information and use it solely to perform under this Agreement. Provider will process Client data in line with applicable data-protection law and its security commitments, and will not use Client data to train third-party models.",
    '',
    '## 7. Limitation of liability',
    '',
    'Neither party is liable for indirect or consequential damages. Each party\'s aggregate liability is limited to the fees paid in the 12 months preceding the claim.',
    '',
    '## 8. Termination',
    '',
    'Either party may terminate for material breach uncured after 30 days\' written notice. On termination, Client pays for services rendered through the termination date and Provider returns or deletes Client data on request.',
    '',
    '## 9. Warranties & disclaimer',
    '',
    'Provider warrants services will be performed in a professional and workmanlike manner consistent with industry standards. EXCEPT AS EXPRESSLY STATED, THE SERVICES AND PLATFORM ARE PROVIDED "AS IS" AND PROVIDER DISCLAIMS ALL OTHER WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE. AI-generated outputs are drafts requiring Client review before reliance.',
    '',
    '## 10. General',
    '',
    '- **Governing law.** This Agreement is governed by the laws of the State of Delaware, without regard to conflicts of law.',
    '- **Independent contractors.** Nothing here creates a partnership, joint venture, or employment relationship.',
    '- **Non-solicitation.** During the term and for 12 months after, neither party will solicit for employment the other\'s personnel directly involved in the engagement.',
    '- **Force majeure.** Neither party is liable for delays caused by events beyond its reasonable control.',
    '- **Notices.** Formal notices go in writing to each party\'s designated contact and are effective on receipt.',
    '- **Assignment.** Neither party may assign this Agreement without consent, except to a successor in a merger or sale of substantially all assets.',
    '- **Entire agreement.** This Agreement plus the referenced proposal/SOW is the complete agreement and supersedes prior discussions; amendments must be in writing and signed by both parties.',
    '',
    '## 11. Signatures',
    '',
    `| Provider — Argo IQ, Inc. | Client — ${client} |`,
    '| --- | --- |',
    `| ${owner} | ${who} |`,
    '| _________________ | _________________ |',
    '| Date: __________ | Date: __________ |',
    '',
    '---',
    '',
    `_${total} committed · effective ${today()}._`,
  ].join('\n');
}
